import { LocationEquipmentTypeData, LocationWizardData } from './types';

/**
 * Helpers for the optional per-equipment-type prices and overage rate. These mirror the
 * API (`LocationService.validateEquipmentTypeOverageRates`, `BookingService.getRate` and
 * `OverageRateResolver`), which treat a blank or $0 equipment price as "use the location's".
 * All amounts are in cents.
 */

type LocationPrices = Pick<LocationWizardData, 'pricePerDay' | 'pricePerMonth'>;
type EquipmentPrices = Pick<LocationEquipmentTypeData, 'pricePerDay' | 'pricePerMonth'>;

/**
 * An equipment price when it is set, else null. Blank and $0 both mean "not set" to the
 * API, which falls back to the location's price.
 */
export const positiveOrNull = (value: number | null | undefined): number | null =>
  typeof value === 'number' && value > 0 ? value : null;

/** Whether an equipment overage rate is set. Blank and $0 both mean "use the location's rate". */
export const hasEquipmentOverageRate = (overageRate: number | null | undefined): overageRate is number =>
  positiveOrNull(overageRate) !== null;

/**
 * Validation message for an optional equipment price, or null when it is valid
 * (including blank / $0, which fall back to the location's price).
 * @param value Parsed price in cents.
 * @param label Lowercase field name used in the message, e.g. "daily price".
 */
export const getEquipmentPriceError = (value: number | null | undefined, label: string): string | null => {
  if (value === null || value === undefined || value === 0) return null;
  // parseCurrencyValue hands back unparseable input (e.g. "abc") as the raw string.
  if (typeof value !== 'number' || !Number.isFinite(value)) return `Please enter a valid ${label}.`;
  if (value < 0) return `${label.charAt(0).toUpperCase()}${label.slice(1)} cannot be negative.`;
  return null;
};

/**
 * Validation message for an equipment overage rate, or null when it is valid
 * (including blank / $0, which fall back to the location's rate). Like the other
 * equipment type prices, any non-negative amount is allowed.
 */
export const getEquipmentOverageRateError = (overageRate: number | null | undefined): string | null =>
  getEquipmentPriceError(overageRate, 'overage rate');

/**
 * First invalid equipment overage rate on the location, prefixed with its
 * equipment type, or null when every rate is valid.
 */
export const getEquipmentOverageRatesError = (
  data: Pick<LocationWizardData, 'locationEquipmentTypes'>
): string | null => {
  for (const equipment of data.locationEquipmentTypes || []) {
    const error = getEquipmentOverageRateError(equipment.overageRate);
    if (error) return `${equipment.equipmentType}: ${error}`;
  }
  return null;
};

/**
 * The rate an equipment type falls back to when it has no overage rate of its own:
 * the location's overage rate, else the equipment type's daily price, else the
 * location's daily price (the same order the API charges with).
 */
export const defaultEquipmentOverageRate = (
  equipment: EquipmentPrices,
  location: LocationPrices & Pick<LocationWizardData, 'overageRate'>
): number =>
  positiveOrNull(location.overageRate)
  ?? positiveOrNull(equipment.pricePerDay)
  ?? positiveOrNull(location.pricePerDay)
  ?? 0;
